import { useCallback, useMemo, useRef, useState } from "react";

// Persist MUI X DataGrid user preferences (column visibility, widths, order,
// density) in localStorage so they survive page refresh and re-login. No
// backend.
//
// Scope the storage key by portal + user so different users on the same
// browser don't clobber each other's preferences.

const readJSON = (key, fallback) => {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded or localStorage unavailable — swallow */
  }
};

// --- Visibility ---------------------------------------------------------
//   const [model, setModel] = usePersistedColumnVisibility(baseKey);
//   <DataGrid columnVisibilityModel={model} onColumnVisibilityModelChange={setModel} />
export const usePersistedColumnVisibility = (baseKey) => {
  const key = baseKey ? `${baseKey}_visibility` : null;
  // Migration: the first ship of this hook stored the visibility model under
  // `fwd_cols_report_${portal}_${user}` (pre-suffix). Try that as a fallback
  // so users don't lose their toggles when the key scheme changed.
  const legacyKey = baseKey
    ? baseKey.replace(/^fwd_grid_report_/, "fwd_cols_report_")
    : null;
  const [model, setModel] = useState(() => {
    const fromNew = readJSON(key, null);
    if (fromNew != null) return fromNew;
    return readJSON(legacyKey, {});
  });

  const handleChange = useCallback(
    (newModel) => {
      setModel(newModel);
      writeJSON(key, newModel);
    },
    [key]
  );

  return [model, handleChange];
};

// --- Column widths ------------------------------------------------------
// Stores map { field: width }. `onColumnWidthChange` gives us params with
// `colDef.field` and `width`. We persist them, and expose the map so the
// canvas-sizing effect in the grid can override its computed width with the
// user's saved width.
//
//   const [widths, handleWidthChange] = usePersistedColumnWidths(baseKey);
//   <DataGrid onColumnWidthChange={handleWidthChange} />
//   // in canvas effect: use widths[field] if present instead of measured
// Internal pseudo-columns (MUI selection checkbox, detail toggle, etc.) that
// should never be width-persisted — letting a stale saved value override the
// grid's built-in compact width is how checkbox columns balloon.
const NON_PERSISTED_FIELDS = new Set(["__check__", "__detail_panel_toggle__", "__tree_data_group__"]);

export const usePersistedColumnWidths = (baseKey) => {
  const key = baseKey ? `${baseKey}_widths` : null;
  const [widths, setWidths] = useState(() => {
    const saved = readJSON(key, {}) || {};
    // One-time cleanup: drop any previously-persisted pseudo-column widths
    // that would otherwise re-inflate the column on load.
    let touched = false;
    for (const f of Object.keys(saved)) {
      if (NON_PERSISTED_FIELDS.has(f)) {
        delete saved[f];
        touched = true;
      }
    }
    if (touched) writeJSON(key, saved);
    return saved;
  });

  const handleChange = useCallback(
    (params) => {
      const field = params?.colDef?.field;
      const width = params?.width;
      if (!field || !width) return;
      if (NON_PERSISTED_FIELDS.has(field)) return;
      setWidths((prev) => {
        const next = { ...prev, [field]: Math.round(width) };
        writeJSON(key, next);
        return next;
      });
    },
    [key]
  );

  return [widths, handleChange];
};

// --- Column order -------------------------------------------------------
// Stores array of field names. Exposes a helper to reorder a columns array
// by the saved order (unknown fields fall to the end, preserving their
// in-code order).
//
//   const { applyOrder, handleOrderChange } = usePersistedColumnOrder(baseKey, apiRef);
//   const orderedCols = applyOrder(myColumns);
//   <DataGrid columns={orderedCols} onColumnOrderChange={handleOrderChange} />
export const usePersistedColumnOrder = (baseKey, apiRef) => {
  const key = baseKey ? `${baseKey}_order` : null;
  const savedOrderRef = useRef(readJSON(key, []));

  const applyOrder = useCallback((columns) => {
    const saved = savedOrderRef.current;
    if (!Array.isArray(saved) || saved.length === 0) return columns;
    const byField = new Map(columns.map((c) => [c.field, c]));
    const ordered = [];
    for (const field of saved) {
      const col = byField.get(field);
      if (col) {
        ordered.push(col);
        byField.delete(field);
      }
    }
    for (const col of byField.values()) ordered.push(col);
    return ordered;
  }, []);

  const handleOrderChange = useCallback(() => {
    // The event fires *after* the grid has applied the reorder internally.
    // Read the current order from the API and persist it.
    if (!apiRef?.current) return;
    try {
      const cols = apiRef.current.getAllColumns?.() || [];
      // Skip the internal checkbox-selection pseudo-column.
      const order = cols
        .map((c) => c.field)
        .filter((f) => f && f !== "__check__");
      savedOrderRef.current = order;
      writeJSON(key, order);
    } catch {
      /* grid not ready */
    }
  }, [apiRef, key]);

  return { applyOrder, handleOrderChange };
};

// --- Density ------------------------------------------------------------
// DataGrid doesn't expose an onDensityChange callback. We use `onStateChange`
// and snapshot density from state. Returns the saved value + a state-change
// handler to attach to the grid.
//
//   const { density, handleStateChange } = usePersistedDensity(baseKey, "compact");
//   <DataGrid initialState={{ density }} onStateChange={handleStateChange} />
export const usePersistedDensity = (baseKey, defaultDensity = "compact") => {
  const key = baseKey ? `${baseKey}_density` : null;
  const initial = readJSON(key, null) || defaultDensity;
  const lastRef = useRef(initial);

  const handleStateChange = useCallback(
    (state) => {
      const next = state?.density?.value;
      if (!next || next === lastRef.current) return;
      lastRef.current = next;
      writeJSON(key, next);
    },
    [key]
  );

  return { density: initial, handleStateChange };
};

// Build a per-user base storage key so a shared browser doesn't leak
// preferences across accounts. Suffixes `_visibility`/`_widths`/`_order`/
// `_density` are appended by each hook.
export const buildColumnVisibilityKey = (portal) => {
  const user = (() => {
    try {
      return localStorage.getItem("current_user") || "anon";
    } catch {
      return "anon";
    }
  })();
  return `fwd_grid_report_${portal}_${user}`;
};
